import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { CSSTransition } from 'react-transition-group'

import S from './Modal.styled'
import { CsCross } from '../Svg'

const Modal = ({
  children,
  isOpen,
  hide,
  modalClass,
  returnFocus,
  isKeyPressed = false,
  isCloseInside = false,
  useCrossIcon = false,
  customCloseIcon,
  customOverlayColor,
  customOnOverlayClick,
}) => {
  const closeModal = () => {
    hide()

    if (returnFocus && isKeyPressed && window.focus) {
      const el = document.querySelector(returnFocus)
      el.focus()
    }
  }

  const onOverlayClick = (event) => {
    if (customOnOverlayClick) {
      customOnOverlayClick(event)
      return
    }
    
    const { target: { classList } } = event
    if (!classList.contains('modal-container')) {
      return ''
    }
    closeModal()
  }

  const onKeyDown = ({ key }) => {
    if (key === 'Escape') {
      closeModal()
    }
  }

  const onClickButtonClick = () => {
    closeModal()
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', onKeyDown)
      document.body.classList.add('modal-open')

      const modalBody = document.querySelector('.modal-body')
      if (window.focus) {
        modalBody.focus()
      }
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown)

      /* check numbers of open modals */
      const openModals = document.querySelectorAll('.modal-container')
      if (openModals.length === 1) {
        document.body.classList.remove('modal-open')
      }
    }
  }, [isOpen])

  if (typeof document === 'undefined') {
    return null
  }

  return (
    <CSSTransition
      in={isOpen}
      timeout={300}
      classNames="modal-transition"
      unmountOnExit
    >
      <>
        {createPortal(
          <S.Modal
            className={`modal-container${
              modalClass ? ` ${modalClass}-container` : ``
            }`}
            isOpen={isOpen}
            onClick={onOverlayClick}
            useCrossIcon={useCrossIcon}
            isCloseInside={isCloseInside}
            customOverlayColor={customOverlayColor}
          >
            <div
              className={`modal-body${modalClass ? ` ${modalClass}-body` : ``}`}
              aria-modal
              aria-hidden
              tabIndex={-1}
              role="dialog"
            >
              {children}

              <button
                type="button"
                className="modal-close-button"
                data-dismiss="modal"
                aria-label="Close"
                onClick={onClickButtonClick}
                onMouseDown={(e) => e.preventDefault()}
              >
                <span aria-hidden="true">
                  {customCloseIcon && customCloseIcon}
                  {useCrossIcon && <CsCross className="icon-cross" />}
                  {!useCrossIcon && !customCloseIcon && (
                    <div className="circle">
                      <div className="lines">
                        <span className="line line1" />
                        <span className="line line2" />
                      </div>
                    </div>
                  )}
                </span>
              </button>
            </div>
          </S.Modal>,
          document.body
        )}
      </>
    </CSSTransition>
  )
}

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  hide: PropTypes.func.isRequired,
  modalClass: PropTypes.string,
  returnFocus: PropTypes.string,
  isKeyPressed: PropTypes.bool,
  isCloseInside: PropTypes.bool,
  useCrossIcon: PropTypes.bool,
  customCloseIcon: PropTypes.node,
  customOverlayColor: PropTypes.string,
  customOnOverlayClick: PropTypes.func,
}

export default Modal
